import { useState, useEffect, useCallback, useRef } from 'react';
import {
  PhoneIncoming, ChevronDown, ChevronRight, HelpCircle, CheckCircle,
  XCircle, Play, Edit, ArrowRight, Calendar, Search, User, Calculator,
  Save, Clock, Home, MessageSquare, Zap, FileText, AlertTriangle,
  RefreshCw, Loader2, Pencil, Plus, Trash2, X, Check
} from 'lucide-react';
import { toast } from 'sonner';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;
const af = (url, opts = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, { ...opts, credentials: "include", headers: { "Content-Type": "application/json", ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
};

const ICON_MAP = {
  PhoneIncoming, HelpCircle, CheckCircle, XCircle, Play, Edit, ArrowRight,
  Calendar, Search, User, Calculator, Save, Clock, Home, MessageSquare,
  Zap, FileText, AlertTriangle
};

const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', dot: 'bg-blue-400' },
  red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', dot: 'bg-red-400' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', dot: 'bg-amber-400' },
  teal: { bg: 'bg-teal-500/10', border: 'border-teal-500/30', text: 'text-teal-400', dot: 'bg-teal-400' },
  purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', dot: 'bg-purple-400' },
  pink: { bg: 'bg-pink-500/10', border: 'border-pink-500/30', text: 'text-pink-400', dot: 'bg-pink-400' },
  slate: { bg: 'bg-slate-500/10', border: 'border-slate-500/30', text: 'text-slate-400', dot: 'bg-slate-400' },
};

const getColor = (c) => COLOR_MAP[c] || COLOR_MAP.slate;

// ─── Etapes selector popover ───
const EtapesEditor = ({ targetEtapes = [], allEtapes, onSave, onClose }) => {
  const [selected, setSelected] = useState(targetEtapes.map(t => t.etape_id));
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const filtered = allEtapes.filter(e =>
    e.id.toLowerCase().includes(search.toLowerCase()) || (e.nom || '').toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSave = () => {
    const result = selected.map(id => {
      const e = allEtapes.find(x => x.id === id);
      return { etape_id: id, etape_nom: e?.nom || id };
    });
    onSave(result);
  };

  return (
    <div ref={ref} className="absolute z-50 top-full left-0 mt-1 w-72 bg-slate-900 border border-slate-600 rounded-lg shadow-2xl p-2" data-testid="etapes-editor-popover">
      <div className="flex items-center gap-1 mb-2">
        <Search size={12} className="text-slate-500" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filtrer les etapes..."
          className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white placeholder:text-slate-600 outline-none focus:border-teal-500/50"
          data-testid="etapes-editor-search"
          autoFocus
        />
      </div>
      <div className="max-h-48 overflow-y-auto space-y-0.5">
        {filtered.map(e => {
          const active = selected.includes(e.id);
          return (
            <button
              key={e.id}
              onClick={() => toggle(e.id)}
              className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 transition-colors ${
                active ? 'bg-teal-500/15 text-teal-300' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
              }`}
              data-testid={`etape-option-${e.id}`}
            >
              <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                active ? 'border-teal-500 bg-teal-500/20' : 'border-slate-600'
              }`}>
                {active && <Check size={9} className="text-teal-400" />}
              </div>
              <span className="font-mono text-[10px] text-slate-500 w-28 truncate flex-shrink-0">{e.id}</span>
              <span className="truncate">{e.nom || e.id}</span>
            </button>
          );
        })}
        {filtered.length === 0 && <p className="text-slate-600 text-[10px] text-center py-2">Aucune etape trouvee</p>}
      </div>
      <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-700">
        <span className="text-[10px] text-slate-500">{selected.length} selectionnee{selected.length > 1 ? 's' : ''}</span>
        <div className="flex gap-1">
          <button onClick={onClose} className="px-2 py-0.5 text-[10px] text-slate-400 hover:text-white bg-slate-800 rounded" data-testid="etapes-editor-cancel">Annuler</button>
          <button onClick={handleSave} className="px-2 py-0.5 text-[10px] text-white bg-teal-600 hover:bg-teal-500 rounded font-medium" data-testid="etapes-editor-save">OK</button>
        </div>
      </div>
    </div>
  );
};

// ─── Target etapes badge (editable) ───
const TargetEtapesBadge = ({ targetEtapes, nodeId, allEtapes, onUpdateTargetEtapes }) => {
  const [editing, setEditing] = useState(false);
  const canEdit = !!onUpdateTargetEtapes;

  return (
    <div className="relative flex flex-wrap items-center gap-1 mt-0.5 ml-5">
      {(targetEtapes || []).map((te, i) => (
        <span key={i} className="px-1.5 py-0.5 bg-teal-500/10 border border-teal-500/30 rounded text-teal-400 text-[9px] font-mono">
          → {te.etape_nom || te.etape_id}
        </span>
      ))}
      {canEdit && (
        <button
          onClick={(e) => { e.stopPropagation(); setEditing(!editing); }}
          className="px-1 py-0.5 bg-slate-700/50 hover:bg-teal-500/20 border border-slate-600 hover:border-teal-500/40 rounded text-slate-400 hover:text-teal-300 transition-colors"
          title="Modifier les etapes cibles"
          data-testid={`edit-target-etapes-${nodeId}`}
        >
          <Pencil size={9} />
        </button>
      )}
      {editing && canEdit && (
        <EtapesEditor
          targetEtapes={targetEtapes || []}
          allEtapes={allEtapes}
          onSave={(newTargets) => { onUpdateTargetEtapes(nodeId, newTargets); setEditing(false); }}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
};

// ─── Recursive tree node renderer ───
const FlowNode = ({ node, depth = 0, expanded, onToggle, allEtapes, onUpdateTargetEtapes }) => {
  if (!node) return null;
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded[node.id] !== false; // default expanded
  const c = getColor(node.color);
  const Icon = ICON_MAP[node.icon] || FileText;
  const indent = depth * 24;

  const canHaveTargets = ['choice', 'final'].includes(node.type);

  // Type-specific rendering
  const renderContent = () => {
    switch (node.type) {
      case 'question':
        return (
          <div className="ml-8 my-1 px-3 py-1.5 bg-blue-500/5 border border-blue-500/20 rounded-lg" style={{ marginLeft: indent + 32 }}>
            <p className="text-blue-300 text-xs font-medium">{node.label}</p>
            {node.hint && <p className="text-blue-400/50 text-[10px] mt-0.5">{node.hint}</p>}
          </div>
        );
      case 'field':
        return (
          <div className="flex items-center gap-2 my-0.5 px-3 py-1" style={{ marginLeft: indent + 32 }}>
            <Edit size={11} className="text-purple-400" />
            <span className="text-purple-300 text-xs">{node.label}</span>
            {node.required && <span className="text-red-400 text-[10px]">*</span>}
            {node.hint && <span className="text-slate-500 text-[10px]">({node.hint})</span>}
          </div>
        );
      case 'action':
        return (
          <div className="flex items-center gap-2 my-0.5 px-3 py-1" style={{ marginLeft: indent + 32 }}>
            <ArrowRight size={11} className={c.text} />
            <span className={`${c.text} text-xs ${node.bold ? 'font-bold' : ''}`}>{node.label}</span>
            {node.hint && <span className="text-slate-500 text-[10px]">({node.hint})</span>}
          </div>
        );
      case 'choice':
        return (
          <div className="my-0.5 px-3 py-1" style={{ marginLeft: indent + 32 }}>
            <div className="flex items-center gap-2">
              <div className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
              <span className={`${c.text} text-xs`}>{node.label}</span>
              {node.result && <span className="text-slate-500 text-[10px]">→ {node.result}</span>}
            </div>
            <TargetEtapesBadge targetEtapes={node.target_etapes} nodeId={node.id} allEtapes={allEtapes} onUpdateTargetEtapes={onUpdateTargetEtapes} />
          </div>
        );
      case 'result':
        return (
          <div className="my-1 px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg" style={{ marginLeft: indent + 32 }}>
            <p className="text-slate-400 text-xs font-medium mb-1">{node.label}</p>
            {(node.results || []).map((r, i) => {
              const rc = getColor(r.color);
              return <p key={i} className={`${rc.text} text-[10px]`}>{r.text}</p>;
            })}
          </div>
        );
      case 'info':
        return (
          <div className="my-1 px-3 py-2 bg-slate-800/30 border border-slate-700/50 rounded-lg" style={{ marginLeft: indent + 32 }}>
            <p className="text-slate-400 text-xs font-medium mb-1">{node.label}</p>
            {(node.items || []).map((item, i) => (
              <p key={i} className="text-slate-500 text-[10px]">- {item}</p>
            ))}
          </div>
        );
      case 'final':
        return (
          <div className={`my-1 px-3 py-1.5 ${c.bg} border ${c.border} rounded-lg`} style={{ marginLeft: indent + 32 }}>
            <p className={`${c.text} text-xs font-bold`}>{node.label}</p>
            <TargetEtapesBadge targetEtapes={node.target_etapes} nodeId={node.id} allEtapes={allEtapes} onUpdateTargetEtapes={onUpdateTargetEtapes} />
          </div>
        );
      default:
        break;
    }

    // Container nodes (root, step, substep, branch_yes, branch_no)
    return (
      <div className="my-1" style={{ marginLeft: indent }}>
        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-slate-800/50 transition-colors ${c.bg} border ${c.border}`}
          onClick={() => hasChildren && onToggle(node.id)}
          data-testid={`flow-node-${node.id}`}
        >
          {hasChildren ? (
            isExpanded ? <ChevronDown size={14} className={c.text} /> : <ChevronRight size={14} className={c.text} />
          ) : <div className="w-3.5" />}
          <Icon size={14} className={c.text} />
          <span className={`${c.text} text-xs font-semibold`}>{node.label}</span>
          {node.sublabel && <span className="text-slate-500 text-[10px] ml-1">— {node.sublabel}</span>}
          <span className="ml-auto text-slate-600 text-[9px] font-mono">{node.type}</span>
        </div>
        {hasChildren && isExpanded && (
          <div className="mt-0.5">
            {node.children.map(child => (
              <FlowNode key={child.id} node={child} depth={depth + 1} expanded={expanded} onToggle={onToggle} allEtapes={allEtapes} onUpdateTargetEtapes={onUpdateTargetEtapes} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // For leaf-type nodes, just render content
  if (['question', 'field', 'action', 'choice', 'result', 'info', 'final'].includes(node.type)) {
    return renderContent();
  }

  return renderContent();
};

// ─── Stats summary ───
const countNodes = (node) => {
  if (!node) return { total: 0, questions: 0, fields: 0, actions: 0, finals: 0 };
  let counts = { total: 1, questions: 0, fields: 0, actions: 0, finals: 0 };
  if (node.type === 'question') counts.questions++;
  if (node.type === 'field') counts.fields++;
  if (node.type === 'action') counts.actions++;
  if (node.type === 'final') counts.finals++;
  (node.children || []).forEach(c => {
    const cc = countNodes(c);
    counts.total += cc.total;
    counts.questions += cc.questions;
    counts.fields += cc.fields;
    counts.actions += cc.actions;
    counts.finals += cc.finals;
  });
  return counts;
};

// ─── Main component ───
const AppelsEntrantsConfigPanel = () => {
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [allEtapes, setAllEtapes] = useState([]);

  const fetchFlow = useCallback(async () => {
    try {
      setLoading(true);
      const res = await af(`${API}/appels-entrants-config/appels-entrants-flow`);
      const data = await res.json();
      setFlow(data.flow);
      setDirty(false);
      // Default all expanded
      const expandAll = {};
      const walk = (n) => { expandAll[n.id] = true; (n.children || []).forEach(walk); };
      if (data.flow) walk(data.flow);
      setExpanded(expandAll);
    } catch (e) {
      toast.error('Erreur chargement configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEtapes = useCallback(async () => {
    try {
      const res = await af(`${API}/config/pipeline/etapes`);
      if (res.ok) {
        const data = await res.json();
        const list = data.etapes || (Array.isArray(data) ? data : (data.data || []));
        setAllEtapes(list);
      }
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchFlow(); fetchEtapes(); }, [fetchFlow, fetchEtapes]);

  // Deep-update target_etapes on a node by id
  const updateNodeTargets = useCallback((nodeId, newTargets) => {
    const updateTree = (node) => {
      if (!node) return node;
      if (node.id === nodeId) return { ...node, target_etapes: newTargets };
      if (node.children) return { ...node, children: node.children.map(updateTree) };
      return node;
    };
    setFlow(prev => updateTree(prev));
    setDirty(true);
  }, []);

  const saveFlow = async () => {
    if (!flow) return;
    try {
      setSaving(true);
      const res = await af(`${API}/appels-entrants-config/appels-entrants-flow`, {
        method: 'PUT',
        body: JSON.stringify({ data: flow })
      });
      if (res.ok) {
        toast.success('Configuration sauvegardee');
        setDirty(false);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (e) {
      toast.error('Erreur reseau');
    } finally {
      setSaving(false);
    }
  };

  const toggleNode = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    const all = {};
    const walk = (n) => { all[n.id] = true; (n.children || []).forEach(walk); };
    if (flow) walk(flow);
    setExpanded(all);
  };

  const collapseAll = () => {
    if (flow) setExpanded({ [flow.id]: true });
  };

  const stats = flow ? countNodes(flow) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-emerald-400" size={24} />
        <span className="ml-2 text-slate-400 text-sm">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="appels-entrants-config-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <PhoneIncoming size={20} className="text-emerald-400" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white">Configuration Appels Entrants</h2>
            <p className="text-xs text-slate-500">Arbre de qualification — flux configurable</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <button
              onClick={saveFlow}
              disabled={saving}
              className="px-3 py-1 text-[10px] font-bold bg-teal-600 hover:bg-teal-500 disabled:bg-teal-800 border border-teal-500 rounded text-white transition-colors flex items-center gap-1"
              data-testid="save-flow-btn"
            >
              {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
              Sauvegarder
            </button>
          )}
          <button onClick={expandAll} className="px-2.5 py-1 text-[10px] bg-slate-800 border border-slate-700 rounded text-slate-400 hover:text-white transition-colors" data-testid="expand-all-btn">
            Tout ouvrir
          </button>
          <button onClick={collapseAll} className="px-2.5 py-1 text-[10px] bg-slate-800 border border-slate-700 rounded text-slate-400 hover:text-white transition-colors" data-testid="collapse-all-btn">
            Tout fermer
          </button>
          <button onClick={fetchFlow} className="px-2.5 py-1 text-[10px] bg-slate-800 border border-slate-700 rounded text-slate-400 hover:text-white transition-colors" data-testid="refresh-flow-btn">
            <RefreshCw size={11} className="inline mr-1" /> Recharger
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="flex gap-3 flex-wrap">
          {[
            { label: 'Noeuds', value: stats.total, color: 'slate' },
            { label: 'Questions', value: stats.questions, color: 'blue' },
            { label: 'Champs', value: stats.fields, color: 'purple' },
            { label: 'Actions', value: stats.actions, color: 'amber' },
            { label: 'Fins', value: stats.finals, color: 'emerald' },
          ].map(s => {
            const c = getColor(s.color);
            return (
              <div key={s.label} className={`px-3 py-1.5 ${c.bg} border ${c.border} rounded-lg`}>
                <span className={`${c.text} text-lg font-bold`}>{s.value}</span>
                <span className="text-slate-500 text-[10px] ml-1.5">{s.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 px-3 py-2 bg-slate-800/30 border border-slate-700/50 rounded-lg">
        <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Legende :</span>
        {[
          { icon: HelpCircle, color: 'blue', label: 'Question' },
          { icon: CheckCircle, color: 'emerald', label: 'OUI' },
          { icon: XCircle, color: 'red', label: 'NON' },
          { icon: Play, color: 'teal', label: 'Etape' },
          { icon: Edit, color: 'purple', label: 'Champ' },
          { icon: ArrowRight, color: 'amber', label: 'Action' },
          { icon: Calendar, color: 'pink', label: 'RDV' },
        ].map(l => {
          const c = getColor(l.color);
          return (
            <div key={l.label} className="flex items-center gap-1">
              <l.icon size={11} className={c.text} />
              <span className={`${c.text} text-[10px]`}>{l.label}</span>
            </div>
          );
        })}
      </div>

      {/* Source badge */}
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${dirty ? 'bg-amber-500/5 border border-amber-500/20' : 'bg-teal-500/5 border border-teal-500/20'}`}>
        <Save size={12} className={dirty ? 'text-amber-400' : 'text-teal-400'} />
        <span className={`text-[10px] ${dirty ? 'text-amber-400' : 'text-teal-400'}`}>
          {dirty ? 'Modifications non sauvegardees — cliquez Sauvegarder' : 'Source : MongoDB (pipeline_config) — non hardcode'}
        </span>
      </div>

      {/* Tree */}
      <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-4 font-mono text-sm overflow-auto max-h-[65vh]">
        {flow ? (
          <FlowNode node={flow} depth={0} expanded={expanded} onToggle={toggleNode} allEtapes={allEtapes} onUpdateTargetEtapes={updateNodeTargets} />
        ) : (
          <p className="text-slate-500 text-center py-8">Aucune configuration trouvee</p>
        )}
      </div>
    </div>
  );
};

export default AppelsEntrantsConfigPanel;
